import { Timestamp, doc, setDoc } from 'firebase/firestore';
import { FirestoreDB } from '../../../utils/firebase/firebase';
import { toast } from 'react-toastify';
import { createClinicalSessionParams } from '../createClinicalSessionParams';
import { updateSessionNote } from '../../../api/sessionNotes';
import { addActivityLog } from '../../../api/documentsAndGoals';
import { safeJsonBtoa } from '../../../utils/encodingUtils';
import { trackSessionChanges } from './sessionChangesTracker';
import { getSessionNotesPathInfo } from './sessionFirestoreHelpers';

/**
 * Submits a clinical session with goals and quick tracks data entries
 * Captures dataEntryIds from Firebase and stores them in session notes
 * Uses createClinicalSessionParams to standardize the session object structure
 */
export const handleSubmitSession = async (
  session,
  sessionData,
  sessionId,
  user,
  goalsDataEntryRef,
  quickTracksDataEntryRef,
  currentUser,
  typeOfUser,
  preRegisteredUsers = [],
  studentGoalsData = [],
  studentBehaviorData = []
) => {
  try {
    // Determine collection name based on user type
    // Use sessionData.studentId as fallback for edit mode
    const { studentId, collectionName } = getSessionNotesPathInfo(
      sessionData,
      user,
      typeOfUser,
      preRegisteredUsers
    );

    // Build goals array in [{goalID: goalId, dataEntryID: dataEntryId}] format
    const goalsArray = (sessionData.selectedGoals || []).map((goalId) => ({
      goalID: goalId,
      dataEntryID: goalsDataEntryRef?.current?.[goalId] || null,
    }));

    // Build quick tracks array in [{quickTrackID: quickTrackId, dataEntryID: dataEntryId}] format
    const quickTracksArray = (sessionData.selectedQuickTracks || []).map(
      (trackId) => ({
        quickTrackID: trackId,
        dataEntryID: quickTracksDataEntryRef?.current?.[trackId] || null,
      })
    );

    // Use the standardized createClinicalSessionParams function
    const sessionNotesObject = createClinicalSessionParams(
      sessionData,
      goalsArray,
      quickTracksArray,
      currentUser,
      sessionData
    );
    const sessionNotesPayload = {
      ...sessionNotesObject,
      status: 'complete',
      sessionStatus: 'complete',
      isDraft: false,
    };

    // Check if we're editing an existing session or creating a new one
    const isEditMode = sessionData?.editingSessionId;
    let sessionNotesId;

    if (isEditMode) {
      // Validate required parameters for update
      if (!studentId || !sessionData.editingSessionId) {
        throw new Error(
          `Missing required parameters (studentId: ${studentId}, sessionNoteId: ${sessionData.editingSessionId})`
        );
      }

      // Update existing session note using API
      const infoValue = btoa(JSON.stringify(sessionNotesPayload));
      const uid = currentUser?.uid || currentUser?.id;

      const updateResult = await updateSessionNote(
        uid,
        typeOfUser,
        studentId,
        sessionData.editingSessionId,
        infoValue
      );

      if (!updateResult.success) {
        throw new Error(
          updateResult.message || 'Failed to update session note'
        );
      }

      sessionNotesId = sessionData.editingSessionId;

      toast.success('Session updated successfully!', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } else {
      if (!sessionId) {
        throw new Error('Missing session ID for new clinical session submission.');
      }

      const sessionDocRef = doc(
        FirestoreDB,
        collectionName,
        studentId,
        'sessionNotes',
        sessionId
      );

      await setDoc(sessionDocRef, {
        info: btoa(JSON.stringify(sessionNotesPayload)),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        isEncrypted: false,
        isDraft: false,
        status: 'complete',
      });

      sessionNotesId = sessionDocRef.id;

      toast.success('Session saved successfully!', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }

    const sessionDate =
      sessionNotesPayload?.ClinicalSessionDetails?.DateOfSession || '';

    // Track changes for edit mode
    let changes = [];
    if (isEditMode && session?.originalData) {
      changes = trackSessionChanges(
        session.originalData,
        sessionNotesPayload,
        studentGoalsData,
        studentBehaviorData
      );
    }

    const activityEntry = {
      actionPerformed: `${
        isEditMode ? 'Updated' : 'Created'
      } clinical session note`,
      editedBy: currentUser?.uid || currentUser?.id || null,
      typeOfActivity: isEditMode ? 'Update Session Note' : 'New Session Note',
      createdAt: Timestamp.now(),
      ...(Array.isArray(changes) && changes.length > 0 ? { changes } : {}),
    };

    const encodedActivityData = safeJsonBtoa(activityEntry);

    await addActivityLog(
      collectionName,
      studentId,
      'sessionNotes',
      sessionNotesId,
      encodedActivityData
    );

    return {
      success: true,
      sessionNotesId: sessionNotesId,
      goalsArray,
      quickTracksArray,
      isUpdate: isEditMode,
    };
  } catch (error) {
    console.error('Error submitting session:', error);
    toast.error('Failed to save session. Please try again.', {
      position: toast.POSITION.BOTTOM_RIGHT,
    });
    throw error;
  }
};
